import { MemberName } from "@/components/MemberName"
import { memberToOutsideMember, deleteMember, resetMemberPassword } from "@/services/member/member"
import { Member } from "@/services/member/member.model"
import { ACCOUNT_TYPES } from "@/utils/constants"
import { ExclamationCircleOutlined } from "@ant-design/icons"
import { Button, Col, Dropdown, Input, Menu, Modal, Row, Table } from "antd"
import { ColumnFilterItem } from "antd/es/table/interface"
import dayjs from "dayjs"
import SettingMember from "../SettingMember"
import { IconFont } from '@/components/IconFont'
import { Organization } from "@/services/organization/organization.model"
import { useMemo, useState } from "react"
import { textIncludes } from "@/utils/utils"
import copy from 'copy-to-clipboard'

const { Column } = Table

interface MemberTableProps {
  loading?: boolean
  data: Member.Info[]
  reload?: () => void
  title?: string
  org: Organization.Info
  actions: React.JSX.Element
}

export const roleFilters: ColumnFilterItem[] = Object.keys(ACCOUNT_TYPES).map((key) => ({
  text: ACCOUNT_TYPES[key].toUpperCase(),
  value: key
}))

export const verifyFilters: ColumnFilterItem[] = [
  { text: 'Varified', value: true },
  { text: 'Not Varified', value: false }
]

export const activeFilters: ColumnFilterItem[] = [
  { text: 'Active', value: true },
  { text: 'Inactive', value: false }
]


export const MemberTable = (props: MemberTableProps) => {
  const {loading, data: members, reload, org: orgData, actions} = props
  const [filterValue, setFilterValue] = useState('')

  const memberRefresh = () => {
    reload?.()
  }

  const filterMembers = useMemo(() => {
    return members
      .filter((member) => textIncludes(member.fullName, filterValue))
  }, [members, filterValue])

  const resetPassword = async (memberId) => {
    const account = await resetMemberPassword(orgData.id, memberId)
    Modal.success({
      title: 'Member',
      content: (
        <div>
          <div>Email: {account.email}</div>
          <div>Password: {account.password}</div>
        </div>
      ),
      okText: 'Copy',
      onOk() {
        copy(`Email: ${account.email}  Password: ${account.password}`)
      }
    })
  }

  return <div>
    <Row justify={'space-between'}>
      <Col>
        <Input
          value={filterValue}
          placeholder='Search'
          className={'mr-1'}
          style={{ width: '200px' }}
          prefix={<IconFont type={'icon-search'} />}
          onChange={(event) => {
            const value = event.target.value
            setFilterValue(value)
          }}
        />
      </Col>
      {actions && <Col>
      {actions}
      </Col>}
    </Row>
    <Table
      className="mt-2"
      bordered
      size='small'
      loading={loading}
      dataSource={filterMembers}
      rowKey={(record) => record.memberId}
      title={() => `${members.length} people in the ${orgData.name}`}
    >
      <Column
        title='Name'
        dataIndex='name'
        key='name'
        align={'center'}
        render={(text, record: Member.Info) => <MemberName member={record} />}
      />

      <Column
        title='Account type'
        dataIndex='accountType'
        key='accountType'
        align={'center'}
        filters={roleFilters}
        onFilter={(value, record: Member.Info) => record.accountType === value}
        render={(value) => (ACCOUNT_TYPES[value] || '').toUpperCase()}
      />

      <Column
        title='Verified Email'
        dataIndex='verified'
        key='personal'
        align={'center'}
        filters={verifyFilters}
        onFilter={(value, record: Member.Info) => record.verified === value}
        render={(value) => (value ? 'Verified' : 'Not Verified')}
      />

      <Column
        title='Active'
        dataIndex='active'
        key='active'
        align={'center'}
        filters={activeFilters}
        onFilter={(value, record: Member.Info) => record.active === value}
        render={(value) => (value ? 'Active' : 'Inactive')}
      />

      <Column
        title='Join time'
        dataIndex='joinTime'
        key='joinTime'
        align={'center'}
        render={(value) => dayjs(value).format('MMM D, YYYY')}
      />

      <Column
        title='Operation'
        dataIndex='title'
        key='title'
        align={'center'}
        render={(_, record: Member.Info) => (
          <Dropdown
            arrow
            overlay={
              <Menu>
                <Menu.Item key='manage' eventKey='manage'>
                  <SettingMember
                    key='manage'
                    member={record}
                    trigger={<span>Manage</span>}
                    onSuccess={() => {
                      memberRefresh()
                    }}
                  />
                </Menu.Item>
                {record.createdBy === 'org' && (
                  <Menu.Item
                    key='reset'
                    onClick={() => {
                      resetPassword(record.memberId)
                    }}
                  >
                    Reset the user's password
                  </Menu.Item>
                )}
                <Menu.Item
                  key='delete'
                  onClick={() => {
                    Modal.confirm({
                      icon: <ExclamationCircleOutlined />,
                      content: 'Are you sure to delete?',
                      onOk: async () => {
                        await deleteMember(orgData.id, record.memberId)
                        memberRefresh()
                      }
                    })
                  }}
                >
                  Remove from organization
                </Menu.Item>
              </Menu>
            }
          >
            <Button size={'small'}>
              <IconFont type='icon-setting' />
            </Button>
          </Dropdown>
        )}
      />
    </Table>
  </div>
  
}